import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Select from 'react-select'
import axios from 'axios'
import VehicleDropdown from '../../shared/common-dropdown/VehicleDropdown'
import PaymentTypeDropdown from '../../shared/common-dropdown/PaymentTypeDropdown'
import VehicleSalesGrid from './VehicleSalesGrid'
import PurchaseVehicleSelectionModal from './PurchaseVehicleSelectionModal'
import { getApiBaseUrl } from '../../utils/apiConfig'
import { getStorageValues } from '../../shared/CommonFunctions'
import { fetchEnquiryById } from '../../utils/enquiryApi'
import { Calendar } from 'primereact/calendar'
import { useMasterData } from '../../utils/useMasterData'
import FinanceDetails from '../../shared/finance/FinanceDetails'
import { calculateReducingRateSummary, parseFinanceValues, validateFinanceValues } from '../../shared/finance/loanCalculator'
import { fetchSalesPaymentReceiptHistory } from '../../utils/salesRegisterApi'
import moment from 'moment'

const getTodayDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const INITIAL_HEADER_STATE = {
  CustomerName: '',
  MobileNumber: '',
  EmailId: '',
  SalesDate: getTodayDate(),
  Remarks: '',
  EnquiryId: null,
  AdvancePayment: '',
  DiscountAmount: '',
}

const INITIAL_DETAIL_STATE = {
  id: null,
  SalesRegisterDetailId: null,
  VehicleId: '',
  VehicleLabel: '',
  VehicleCost: '',
  PaymentType: 'cash',
  PaymentTypeLabel: 'Cash',
  DownPayment: '',
  EMITenure: '',
  EMIAmount: '',
  ProcessingFees: '',
  InterestRate: '',
  ChassisNo: '',
  EngineNo: '',
  Selected: false
}

function AddSalesRegister({ sendDataToParent, onLoad, initialValues = null, onLoanSummary, onRegisterLoanSummaryApply, onlyFromEnquiry = false }) {
  const [headerState, setHeaderState] = useState(INITIAL_HEADER_STATE)
  const [detailForm, setDetailForm] = useState(INITIAL_DETAIL_STATE)
  const [detailEntries, setDetailEntries] = useState([])
  const [vehicleError, setVehicleError] = useState('')
  const [duplicateError, setDuplicateError] = useState('')
  const [showPurchaseModal, setShowPurchaseModal] = useState(false)
  const [purchaseVehicleId, setPurchaseVehicleId] = useState(null)
  const [purchaseVehicleLabel, setPurchaseVehicleLabel] = useState('')
  const [purchaseTargetRowId, setPurchaseTargetRowId] = useState(null)
  const initialValuesKeyRef = useRef(null)
  const [enquiryOptions, setEnquiryOptions] = useState([])
  const [selectedEnquiry, setSelectedEnquiry] = useState(null)
  const [loanSummaryError, setLoanSummaryError] = useState('')
  const [financePartners, setFinancePartners] = useState([])
  const [financePartnersError, setFinancePartnersError] = useState('')
  const [selectedFinancePartnerId, setSelectedFinancePartnerId] = useState(null)
  const skipDetailSyncRef = useRef(false)
  const { items: interestRateItems, loading: interestRateLoading } = useMasterData('interestRates')
  const [accessoriesItems, setAccessoriesItems] = useState([])
  const [accessoriesLoading, setAccessoriesLoading] = useState(false)
  const [accessoriesError, setAccessoriesError] = useState('')
  const [paymentHistory, setPaymentHistory] = useState([])

  const interestRateOptions = useMemo(() => {
    return (interestRateItems || []).map((item) => ({
      value: item.ValueName ?? '',
      label: item.DisplayName || item.ValueName || '',
      id: item.MasterValueId
    }))
  }, [interestRateItems])

  const selectedInterestRate = useMemo(() => {
    if (!detailForm.InterestRate) return null
    const valueStr = detailForm.InterestRate.toString()
    return interestRateOptions.find((opt) => opt.value?.toString() === valueStr) || null
  }, [detailForm.InterestRate, interestRateOptions])

  const fetchFinancePartners = useCallback(async () => {
    try {
      setFinancePartnersError('')
      const response = await axios.get(`${getApiBaseUrl()}/financepartner/getAll`, {
        params: { UserId: getStorageValues('userId') }
      })
      const payload = response?.data?.data
      const rows = Array.isArray(payload?.[0]) ? payload[0] : Array.isArray(payload) ? payload : []
      setFinancePartners(rows)
    } catch (error) {
      setFinancePartners([])
      setFinancePartnersError('Unable to load finance partners.')
    }
  }, [])

  const fetchAccessories = useCallback(async () => {
    try {
      setAccessoriesLoading(true)
      setAccessoriesError('')
      const response = await axios.get(`${getApiBaseUrl()}/handoveritems/getAll`, {
        params: { UserId: getStorageValues('userId') }
      })
      const payload = response?.data?.data
      const rows = Array.isArray(payload?.[0]) ? payload[0] : Array.isArray(payload) ? payload : []
      const accessoryRows = rows.filter((item) => (item.HandoverItemType || '').toLowerCase() === 'accessories')
      setAccessoriesItems(accessoryRows)
    } catch (error) {
      setAccessoriesItems([])
      setAccessoriesError('Unable to load accessories.')
    } finally {
      setAccessoriesLoading(false)
    }
  }, [])
  const updateHeaderField = useCallback((name, value) => {
    setHeaderState((prev) => ({ ...prev, [name]: value }))
  }, [sendDataToParent])

  const handleHeaderChange = (e) => {
    const { name, value } = e.currentTarget
    updateHeaderField(name, value)
  }

  const formatHeaderMoneyField = useCallback((fieldName) => {
    const raw = headerState?.[fieldName]
    const str = String(raw ?? '').replace(/,/g, '').trim()
    if (!str) {
      updateHeaderField(fieldName, '')
      return
    }
    const n = Number(str)
    if (!Number.isFinite(n)) {
      updateHeaderField(fieldName, '')
      return
    }
    updateHeaderField(fieldName, n.toFixed(2))
  }, [headerState, updateHeaderField])

  const handleSalesDateChange = (event) => {
    const dateValue = event?.value
    if (!dateValue) {
      updateHeaderField('SalesDate', '')
      return
    }
    const year = dateValue.getFullYear()
    const month = String(dateValue.getMonth() + 1).padStart(2, '0')
    const day = String(dateValue.getDate()).padStart(2, '0')
    updateHeaderField('SalesDate', `${year}-${month}-${day}`)
  }

  const handleDetailField = (name, value, options = {}) => {
    setDetailForm((prev) => {
      if (options.skipParent) {
        skipDetailSyncRef.current = true
      }
      return { ...prev, [name]: value }
    })
  }

  const handleVehicleChange = (option) => {
    const value = option ? option.value : ''
    const label = option ? option.label : ''
    handleDetailField('VehicleId', value, { skipParent: true })
    handleDetailField('VehicleLabel', label, { skipParent: true })
    setVehicleError('')
    setDuplicateError('')
    if (value) {
      setPurchaseVehicleId(value)
      setPurchaseVehicleLabel(label)
      setPurchaseTargetRowId(null)
      setShowPurchaseModal(true)
    }
  }

  const handlePaymentTypeChange = (option) => {
    const value = option ? option.value : ''
    const label = option ? option.label : ''
    handleDetailField('PaymentType', value)
    handleDetailField('PaymentTypeLabel', label, { skipParent: true })
    if (value !== 'loan') {
      handleDetailField('DownPayment', '', { skipParent: true })
      handleDetailField('EMITenure', '', { skipParent: true })
      handleDetailField('EMIAmount', '', { skipParent: true })
      handleDetailField('ProcessingFees', '', { skipParent: true })
      handleDetailField('InterestRate', '', { skipParent: true })
      setSelectedFinancePartnerId(null)
    }
  }

  const handleFinancePartnerClick = (partner) => {
    if (!partner) return
    const rate = partner.InterestRate ?? ''
    const downPayment = partner.MinimumDownPayment ?? ''
    handleDetailField('InterestRate', rate === null ? '' : rate.toString())
    handleDetailField('DownPayment', downPayment === null ? '' : downPayment.toString())
    const key = partner.FinancePartnerId ?? partner.FinancePartnerName ?? null
    setSelectedFinancePartnerId(key)
  }

  useEffect(() => {
    fetchAccessories()
  }, [fetchAccessories])

  const openPurchaseModalForRow = (row) => {
    if (!row) return
    setPurchaseVehicleId(row.VehicleId || null)
    setPurchaseVehicleLabel(row.VehicleLabel || row.Vehicle || '')
    setPurchaseTargetRowId(row.id || null)
    setShowPurchaseModal(true)
  }

  const handlePurchaseContinue = (row) => {
    if (!row) return
    const isFromEnquiry = Boolean(headerState?.EnquiryId)
    if (purchaseTargetRowId) {
      updateDetailEntries((prev) => prev.map((entry) => (
        entry.id === purchaseTargetRowId
          ? {
            ...entry,
            ChassisNo: row.ChassisNo || entry.ChassisNo,
            EngineNo: row.EngineNo || entry.EngineNo,
            VehicleCost: (
              isFromEnquiry
                ? entry.VehicleCost
                : ((entry.VehicleCost === '' || entry.VehicleCost === null || entry.VehicleCost === undefined)
                  && row.VehicleCost !== undefined
                  && row.VehicleCost !== null)
                  ? row.VehicleCost
                  : entry.VehicleCost
            )
          }
          : entry
      )))
    } else {
      handleDetailField('ChassisNo', row.ChassisNo || '', { skipParent: true })
      handleDetailField('EngineNo', row.EngineNo || '', { skipParent: true })
      if (!isFromEnquiry) {
        const currentCost = detailForm?.VehicleCost
        const isEmpty = currentCost === '' || currentCost === null || currentCost === undefined
        if (isEmpty && row.VehicleCost !== undefined && row.VehicleCost !== null) {
          handleDetailField('VehicleCost', row.VehicleCost, { skipParent: true })
        }
      }
    }
    setShowPurchaseModal(false)
  }

  const closePurchaseModal = () => {
    setShowPurchaseModal(false)
  }

  const resetDetailForm = useCallback(() => {
    setDetailForm(INITIAL_DETAIL_STATE)
    setVehicleError('')
    setDuplicateError('')
    setSelectedFinancePartnerId(null)
  }, [])

  const updateDetailEntries = useCallback((updater) => {
    setDetailEntries((prev) => {
      const next = updater(prev)
      sendDataToParent((parentPrev) => ({ ...parentPrev, SalesRegisterDetails: next }))
      return next
    })
  }, [sendDataToParent])

  const addOrUpdateDetail = (e) => {
    e?.preventDefault()
    if (!detailForm.VehicleId) {
      setVehicleError('Vehicle is required')
      return
    }

    const duplicate = detailEntries.some((entry) => entry.VehicleId === detailForm.VehicleId && entry.id !== detailForm.id)
    if (duplicate) {
      setDuplicateError('An entry for this vehicle already exists.')
      return
    }

    const payload = {
      ...detailForm,
      id: detailForm.id || detailForm.SalesRegisterDetailId || Date.now(),
      Vehicle: detailForm.VehicleLabel || detailForm.Vehicle,
      PaymentType: detailForm.PaymentTypeLabel || detailForm.PaymentType,
      Selected: true
    }

    updateDetailEntries((prev) => {
      const next = detailForm.id
        ? prev.map((row) => (row.id === detailForm.id ? payload : row))
        : [...prev, payload]
      return next
    })

    resetDetailForm()
  }


  const handleEntrySelection = (row, isSelected) => {
    if (!row) return
    updateDetailEntries((prev) => prev.map((entry) => (entry.id === row.id ? { ...entry, Selected: isSelected } : entry)))
    if (isSelected) {
      openPurchaseModalForRow(row)
    }
  }

  const handleEntryFieldChange = (rowId, fieldName, value) => {
    updateDetailEntries((prev) => prev.map((entry) => (entry.id === rowId ? { ...entry, [fieldName]: value } : entry)))
  }

  const handleDeleteDetail = (row) => {
    updateDetailEntries((prev) => prev.filter((entry) => entry.id !== row.id))
  }

  useEffect(() => {
    const ADD_MODE_KEY = '__add__'
    const incomingKey = initialValues?.SalesRegisterId ?? (initialValues ? '__static__' : null)

    if (initialValues) {
      if (initialValuesKeyRef.current === incomingKey) return

      const header = {
        CustomerName: initialValues.CustomerName ?? '',
        MobileNumber: initialValues.MobileNumber ?? '',
        EmailId: initialValues.EmailId ?? '',
        SalesDate: initialValues.SalesDate ?? '',
        Remarks: initialValues.Remarks ?? '',
        EnquiryId: initialValues.EnquiryId ?? null,
        AdvancePayment: initialValues.AdvancePayment ?? '',
        DiscountAmount: initialValues.DiscountAmount ?? '',
      }
      setHeaderState({ ...INITIAL_HEADER_STATE, ...header })
      const details = (initialValues.SalesRegisterDetails || []).map((d) => ({
        ...d,
        id: d.id || d.SalesRegisterDetailId || Date.now() + Math.random(),
        VehicleLabel: d.VehicleLabel || d.Vehicle || d.VehicleNameValue || '',
        PaymentType: d.PaymentType,
        Selected: d.Selected !== undefined ? d.Selected : true
      }))
      setDetailEntries(details)
      sendDataToParent((prev) => ({ ...prev, ...header, SalesRegisterDetails: details }))
      resetDetailForm()
      initialValuesKeyRef.current = incomingKey
      return
    }

    if (initialValuesKeyRef.current === ADD_MODE_KEY) return

    onLoad?.(undefined)
    setHeaderState({ ...INITIAL_HEADER_STATE })
    setDetailEntries([])
    resetDetailForm()
    sendDataToParent((prev) => ({ ...prev, ...INITIAL_HEADER_STATE, SalesRegisterDetails: [] }))
    initialValuesKeyRef.current = ADD_MODE_KEY
  }, [initialValues, onLoad, resetDetailForm, sendDataToParent])

  const financeFieldsVisible = useMemo(() => detailForm.PaymentType === 'loan', [detailForm.PaymentType])
  const isEnquirySelected = Boolean(headerState.EnquiryId)
  const isEditMode = Boolean(initialValues?.SalesRegisterId)
  const isEnquiryRequiredForManualEntry = onlyFromEnquiry && !isEnquirySelected
  const hideAddForm = (isEnquirySelected && !isEditMode) || isEnquiryRequiredForManualEntry

  const hasLoanEntry = useMemo(() => {
    return (detailEntries || []).some((row) => String(row?.PaymentType || '').toLowerCase() === 'loan')
  }, [detailEntries])

  const totals = useMemo(() => {
    const totalVehicleCost = (detailEntries || []).reduce((sum, row) => {
      const n = Number(String(row?.VehicleCost ?? '').replace(/,/g, '').trim())
      return sum + (Number.isFinite(n) ? n : 0)
    }, 0)
    const advance = Number(String(headerState.AdvancePayment ?? '').replace(/,/g, '').trim())
    const discount = Number(String(headerState.DiscountAmount ?? '').replace(/,/g, '').trim())
    const safeAdvance = Number.isFinite(advance) ? advance : 0
    const safeDiscount = Number.isFinite(discount) ? discount : 0
    const totalReceipts = (paymentHistory || []).reduce((sum, row) => {
      if (String(row?.PaymentTypeLabel || '').toLowerCase() !== 'receipt') return sum
      const n = Number(String(row?.Amount ?? '').replace(/,/g, '').trim())
      return sum + (Number.isFinite(n) ? n : 0)
    }, 0)
    const pending = Math.max(0, totalVehicleCost - safeAdvance - safeDiscount - totalReceipts)
    return { totalVehicleCost, advance: safeAdvance, discount: safeDiscount, totalReceipts, pending }
  }, [detailEntries, headerState.AdvancePayment, headerState.DiscountAmount, paymentHistory])

  useEffect(() => {
    const salesRegisterId = initialValues?.SalesRegisterId
    const userId = getStorageValues('userId')

    if (!isEditMode || !salesRegisterId) {
      setPaymentHistory([])
      return
    }

    let cancelled = false
    ;(async () => {
      try {
        const list = await fetchSalesPaymentReceiptHistory({ userId, salesRegisterId })
        if (!cancelled) setPaymentHistory(Array.isArray(list) ? list : [])
      } catch (err) {
        if (!cancelled) setPaymentHistory([])
      }
    })()

    return () => {
      cancelled = true
    }
  }, [initialValues?.SalesRegisterId, isEditMode])

  const parsedSalesDate = useMemo(() => {
    if (!headerState.SalesDate) return null
    const date = headerState.SalesDate instanceof Date
      ? headerState.SalesDate
      : new Date(headerState.SalesDate)
    return Number.isNaN(date.getTime()) ? null : date
  }, [headerState.SalesDate])

  const parsedFinanceValues = useMemo(() => parseFinanceValues(detailForm), [
    detailForm.VehicleCost,
    detailForm.DownPayment,
    detailForm.EMITenure,
    detailForm.ProcessingFees,
    detailForm.InterestRate
  ])

  const calculateLoanSummary = () => {
    validateFinanceValues(parsedFinanceValues)
    return calculateReducingRateSummary(parsedFinanceValues)
  }

  const handleLoanSummaryClick = () => {
    try {
      const summary = calculateLoanSummary()
      setLoanSummaryError('')
      if (typeof onLoanSummary === 'function') {
        onLoanSummary(summary)
      }
    } catch (err) {
      setLoanSummaryError(err.message || 'Unable to calculate loan summary.')
    }
  }

  const applyLoanSummaryEmi = useCallback((summary) => {
    if (!summary) {
      return
    }
    const emiValue = summary.monthlyEmi ?? ''
    handleDetailField('EMIAmount', emiValue !== '' ? String(emiValue) : '', { skipParent: false })
  }, [handleDetailField])

  useEffect(() => {
    if (typeof onRegisterLoanSummaryApply === 'function') {
      onRegisterLoanSummaryApply(applyLoanSummaryEmi)
      return () => onRegisterLoanSummaryApply(null)
    }
  }, [applyLoanSummaryEmi, onRegisterLoanSummaryApply])

  useEffect(() => {
    sendDataToParent((parentPrev) => ({ ...parentPrev, ...headerState }))
  }, [headerState, sendDataToParent])

  useEffect(() => {
    if (skipDetailSyncRef.current) {
      skipDetailSyncRef.current = false
      return
    }
    sendDataToParent((parentPrev) => ({ ...parentPrev, CurrentDetail: detailForm }))
  }, [detailForm, sendDataToParent])

  useEffect(() => {
    fetchFinancePartners()
  }, [fetchFinancePartners])

  useEffect(() => {
    const fetchEnquiries = async () => {
      try {
        const isForDropdown = isEditMode ? '0' : '1'
        const response = await axios.get(`${getApiBaseUrl()}/enquiry/getall`, {
          params: { UserId: getStorageValues('userId'), IsForDropodown: isForDropdown }
        })
        const rows = response?.data?.data
        const list = Array.isArray(rows) ? rows : []

        const toOption = (row) => {
          const enquiryNumber = row?.EnquiryNumber
            || (() => {
              const d = row?.CreatedDate ? new Date(row.CreatedDate) : null
              const mmdd = d && !isNaN(d.getTime())
                ? `${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`
                : '0000'
              const paddedId = String(row?.EnquiryId ?? '').padStart(4, '0')
              return `ENQ/${mmdd}/${paddedId}`
            })()
          const name = row?.CustomerName ? ` (${row.CustomerName})` : ''
          return {
            value: row?.EnquiryId ?? null,
            label: `${enquiryNumber}${name}`,
            enquiryNumber,
            customerName: row?.CustomerName || '',
            mobile: row?.MobileNo || ''
          }
        }

        let opts = list.map(toOption).filter((opt) => opt.value !== null)

        const currentEnquiryId = headerState.EnquiryId
        if (currentEnquiryId && !opts.some((opt) => opt.value === currentEnquiryId)) {
          try {
            const data = await fetchEnquiryById({ userId: getStorageValues('userId'), enquiryId: currentEnquiryId })
            const header = data?.header
            if (header) {
              const extra = toOption({
                EnquiryId: header.EnquiryId ?? currentEnquiryId,
                EnquiryNumber: header.EnquiryNumber,
                CustomerName: header.CustomerName,
                MobileNo: header.MobileNo,
                CreatedDate: header.CreatedDate
              })
              if (extra.value !== null) {
                opts = [extra, ...opts]
              }
            }
          } catch (err) {
            // ignore fallback failures; dropdown will still render loaded list
          }
        }

        setEnquiryOptions(opts)
      } catch (error) {
        console.error('Failed to load enquiries', error)
      }
    }
    fetchEnquiries()
  }, [headerState.EnquiryId, isEditMode])

  useEffect(() => {
    const enquiryId = headerState.EnquiryId
    if (!enquiryId) {
      if (selectedEnquiry) setSelectedEnquiry(null)
      return
    }
    if (enquiryOptions.length === 0) return
    const match = enquiryOptions.find((opt) => opt.value === enquiryId) || null
    if (match && match.value !== selectedEnquiry?.value) {
      setSelectedEnquiry(match)
    }
  }, [enquiryOptions, headerState.EnquiryId, selectedEnquiry])

  const handleEnquirySelect = async (option) => {
    setSelectedEnquiry(option)
    const enquiryId = option ? option.value : null
    updateHeaderField('EnquiryId', enquiryId)

    if (option) {
      try {
        const data = await fetchEnquiryById({ userId: getStorageValues('userId'), enquiryId })
        const name = option.customerName || data?.header?.CustomerName || ''
        const mobile = option.mobile || data?.header?.MobileNo || ''
        setHeaderState((prev) => ({ ...prev, CustomerName: name, MobileNumber: mobile }))
        sendDataToParent((prev) => ({ ...prev, CustomerName: name, MobileNumber: mobile, EnquiryId: enquiryId }))

        const detailRows = (data?.details || []).map((d) => ({
          id: d.SalesRegisterDetailId || d.id || Date.now() + Math.random(),
          SalesRegisterDetailId: d.SalesRegisterDetailId || null,
          VehicleId: d.VehicleId,
          VehicleLabel: d.VehicleLabel || d.VehicleNameValue || d.VehicleName || '',
          VehicleCost: d.VehicleCost,
          PaymentType: d.PaymentType,
          DownPayment: d.DownPayment,
          EMITenure: d.EMITenure,
          EMIAmount: d.EMIAmount,
          ProcessingFees: d.ProcessingFees,
          InterestRate: d.InterestRate || '',
          ChassisNo: d.ChassisNo || '',
          EngineNo: d.EngineNo || '',
          PaymentTypeValue: d.PaymentType,
          VehicleNameValue: d.VehicleNameValue || d.VehicleLabel || '',
          Selected: false,
        }))
        setDetailEntries(detailRows)
        sendDataToParent((prev) => ({ ...prev, SalesRegisterDetails: detailRows }))
      } catch (error) {
        console.error('Failed to load enquiry details', error)
      }
    } else {
      setDetailEntries([])
      sendDataToParent((prev) => ({ ...prev, SalesRegisterDetails: [], CustomerName: '', MobileNumber: '' }))
      setHeaderState((prev) => ({ ...prev, CustomerName: '', MobileNumber: '' }))
    }
  }

  return (
    <>
      <div className="row">
        <div className="col-md-12">

          <div className="card-header">
            <div className="card-title"></div>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-lg-9 col-sm-9 col-12 mb-3">
                <label className="form-label">From Enquiry{onlyFromEnquiry && <span style={{ color: 'red' }}>*</span>}</label>
                <Select
                  value={selectedEnquiry}
                  onChange={handleEnquirySelect}
                  options={enquiryOptions}
                  placeholder="Select enquiry"
                  isClearable={!isEditMode}
                  isDisabled={isEditMode}
                />
              </div>
              <div className="col-lg-3 col-sm-3 col-12 mb-3">
                <label className="form-label">Sales Date</label>
                <Calendar
                  name="SalesDate"
                  value={parsedSalesDate}
                  onChange={handleSalesDateChange}
                  maxDate={new Date()}
                  dateFormat="dd-mm-yy"
                  showIcon
                  inputStyle={{ height: '40px' }}
                />
                
              </div>
              <div className="col-lg-9 col-sm-9 col-12 mb-3">
                <label className="form-label">Customer Name<span style={{ color: 'red' }}>*</span></label>
                <input
                  type="text"
                  className="form-control"
                  name="CustomerName"
                  value={headerState.CustomerName}
                  onChange={handleHeaderChange}
                />
              </div>
              <div className="col-lg-3 col-sm-3 col-12 mb-3">
                <label className="form-label">Mobile No</label>
                <input
                  type="text"
                  className="form-control"
                  name="MobileNumber"
                  value={headerState.MobileNumber}
                  onChange={handleHeaderChange}
                />
              </div>


            </div>

            {!hideAddForm && (
              <>
                <div className="row">
                  <div className="col-lg-6 col-sm-6 col-12 mb-3">
                    <VehicleDropdown
                      onChangeDropdown={handleVehicleChange}
                      selectedValue={detailForm.VehicleId}
                    />
                    {vehicleError && <div className="text-danger small mt-1">{vehicleError}</div>}
                  </div>
                  <div className="col-lg-3 col-sm-6 col-12 mb-3">
                    <label className="form-label">Vehicle Cost</label>
                    <input
                      type="text"
                      className="form-control"
                      name="VehicleCost"
                      value={detailForm.VehicleCost}
                      onChange={(e) => handleDetailField('VehicleCost', e.currentTarget.value)}
                    />
                  </div>
                  <div className="col-lg-3 col-sm-6 col-12 mb-3">
                    <PaymentTypeDropdown
                      onChangeDropdown={handlePaymentTypeChange}
                      selectedValue={detailForm.PaymentType}
                    />
                  </div>

                  {financeFieldsVisible && (
                    
                    <div className="col-12">
                      <FinanceDetails
                        financePartners={financePartners}
                        financePartnersError={financePartnersError}
                        selectedFinancePartnerId={selectedFinancePartnerId}
                        onFinancePartnerClick={handleFinancePartnerClick}
                        downPaymentValue={detailForm.DownPayment}
                        emiTenureValue={detailForm.EMITenure}
                        processingFeesValue={detailForm.ProcessingFees}
                        interestRateValue={detailForm.InterestRate}
                        emiAmountValue={detailForm.EMIAmount}
                        onDownPaymentChange={(e) => handleDetailField('DownPayment', e.currentTarget.value)}
                        onEmiTenureChange={(e) => handleDetailField('EMITenure', e.currentTarget.value)}
                        onProcessingFeesChange={(e) => handleDetailField('ProcessingFees', e.currentTarget.value)}
                        onInterestRateChange={(option) => handleDetailField('InterestRate', option ? option.value : '')}
                        onEmiAmountChange={(e) => handleDetailField('EMIAmount', e.currentTarget.value)}
                        interestRateOptions={interestRateOptions}
                        interestRateLoading={interestRateLoading}
                        selectedInterestRate={selectedInterestRate}
                        onCalculateEmi={handleLoanSummaryClick}
                        loanSummaryError={loanSummaryError}
                      />
                    </div>
                  )}
                </div>
                <div className="enq-form mb-4">
                  <button type="button" className="btn btn-cancel me-2" onClick={resetDetailForm}>
                    {detailForm.id ? 'Cancel Edit' : 'Reset'}
                  </button>
                  <button type="button" className="btn btn-submit" onClick={addOrUpdateDetail}>
                    {detailForm.id ? 'Update Vehicle' : 'Add Vehicle'}
                  </button>
                  {duplicateError && <div className="text-danger small mt-2">{duplicateError}</div>}
                </div>
              </>
            )}

              <div className="card-body">
                <nav className="nav nav-style-6 nav-pills mb-3 nav-justified d-sm-flex d-block" role="tablist">
                  <a className="nav-link active" data-bs-toggle="tab" role="tab" aria-current="page" href="#nav-products-justified" aria-selected="true">VEHICLE SALES DETAILS</a>
                  <a className="nav-link" data-bs-toggle="tab" role="tab" href="#nav-cart-justified" aria-selected="false" tabIndex="-1">VEHICLE ACCESSORIES </a>

                </nav>
                <div className="tab-content">
                  <div className="tab-pane text-muted active show" id="nav-products-justified" role="tabpanel">
                    <div className="col-12">
                      <VehicleSalesGrid
                        data={detailEntries}
                        onSelectEntry={handleEntrySelection}
                        onFieldChange={handleEntryFieldChange}
                        onDelete={handleDeleteDetail}
                      />
                    </div>

                    {detailEntries.length > 0 && !hasLoanEntry && (
                      <div className="row mt-3">
                        <div className="col-lg-8 col-12 mb-2">
                          <div className="card table-list-card">
                            <div className="card-body">
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <div className="fw-semibold">Payment Receipts</div>
                                <div className="text-muted small">Total Receipts: {Number(totals.totalReceipts || 0).toFixed(2)}</div>
                              </div>
                              <div className="table-responsive" style={{ maxHeight: '180px', overflowY: 'auto' }}>
                                <table className="table table-sm table-bordered mb-0">
                                  <thead>
                                    <tr>
                                      <th style={{ width: '140px' }}>Date</th>
                                      <th>Remarks</th>
                                      <th style={{ width: '140px', textAlign: 'right' }}>Amount</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {(paymentHistory || []).filter((p) => String(p?.PaymentTypeLabel || '').toLowerCase() === 'receipt').map((p, i) => {
                                      const dateStr = p?.PaymentDate ? moment(p.PaymentDate).format('DD/MM/YYYY') : '-'
                                      const amount = Number(p?.Amount || 0)
                                      return (
                                        <tr key={`${p?.PaymentDate || 'receipt'}-${p?.Amount || ''}-${i}`}>
                                          <td>{dateStr}</td>
                                          <td>{p?.Remarks || '-'}</td>
                                          <td style={{ textAlign: 'right' }}>{Number.isFinite(amount) ? amount.toFixed(2) : '0.00'}</td>
                                        </tr>
                                      )
                                    })}
                                    {(paymentHistory || []).some((p) => String(p?.PaymentTypeLabel || '').toLowerCase() === 'receipt') && (
                                      <tr>
                                        <td style={{ fontWeight: 700 }} colSpan={2}>Total Paid Amount</td>
                                        <td style={{ textAlign: 'right', fontWeight: 700 }}>
                                          {Number((totals.totalReceipts || 0) + (totals.advance || 0) + (totals.discount || 0)).toFixed(2)}
                                        </td>
                                      </tr>
                                    )}
                                    {(!(paymentHistory || []).some((p) => String(p?.PaymentTypeLabel || '').toLowerCase() === 'receipt')) && (
                                      <tr>
                                        <td colSpan={3} className="text-muted small">No receipts yet.</td>
                                      </tr>
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-lg-4 col-md-5 col-sm-6 col-12 ms-auto">
                          <div className="d-flex flex-column gap-2">
                            <div className="d-flex align-items-center gap-2">
                              <div className="form-label mb-0" style={{ minWidth: '160px' }}>Advance Payment:</div>
                              <input
                                type="text"
                                className="form-control"
                                name="AdvancePayment"
                                value={headerState.AdvancePayment ?? ''}
                                onChange={handleHeaderChange}
                                style={{ textAlign: 'right' }}
                                onBlur={() => formatHeaderMoneyField('AdvancePayment')}
                              />
                            </div>
                            <div className="d-flex align-items-center gap-2">
                              <div className="form-label mb-0" style={{ minWidth: '160px' }}>Discount Amount:</div>
                              <input
                                type="text"
                                className="form-control"
                                name="DiscountAmount"
                                value={headerState.DiscountAmount ?? ''}
                                onChange={handleHeaderChange}
                                style={{ textAlign: 'right' }}
                                onBlur={() => formatHeaderMoneyField('DiscountAmount')}
                              />
                            </div>
                            <div className="d-flex align-items-center gap-2">
                              <div className="form-label mb-0" style={{ minWidth: '160px', fontWeight: 700 }}>Pending Amount:</div>
                              <div className="form-control" style={{ fontWeight: 700, textAlign: 'right' }}>
                                {totals.pending.toFixed(2)}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="tab-pane text-muted" id="nav-cart-justified" role="tabpanel">
                    <div className="acces-box">
                      <div className="acess">
                        {accessoriesLoading && (
                          <p className="col-12 text-muted">Loading accessories…</p>
                        )}
                        {!accessoriesLoading && accessoriesItems?.length === 0 && (
                          <p className="col-12 text-muted">No accessories configured.</p>
                        )}
                        {accessoriesError && (
                          <p className="col-12 text-danger">{accessoriesError}</p>
                        )}
                        {(accessoriesItems || []).map((item) => (
                          <p className="col-md-2" key={item.HandoverItemId || item.HandoverItemTitle}>
                            {(item.HandoverItemTitle || '').toUpperCase()}
                          </p>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-lg-12 col-sm-12 col-12 mb-3">
                  <label className="form-label">Remarks</label>
                  <input
                    type="text"
                    className="form-control"
                    name="Remarks"
                    value={headerState.Remarks}
                    onChange={handleHeaderChange}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <PurchaseVehicleSelectionModal
          show={showPurchaseModal}
          onClose={closePurchaseModal}
          onContinue={handlePurchaseContinue}
          vehicleId={purchaseVehicleId}
          vehicleLabel={purchaseVehicleLabel}
          excludeKeys={detailEntries.map((entry) => `${(entry.EngineNo || '').toString().trim()}|${(entry.ChassisNo || '').toString().trim()}`)}
        />
    </>
  )
}

export default AddSalesRegister
